import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Column, Row, Select } from "../../../../components";
import { RootState } from "../../../../shared/store";
import { FloatingLabel, Form } from "react-bootstrap";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { PolicyFloodCoverageDto } from "../../../model/PolicyFloodCoverageDto";

export interface ICustomerFloodCoverageForm {
    customerId?: string;
    floodCoverageId?: string;
    polEventId?: string;
    polId?: string;
    customerFloodCoverage: PolicyFloodCoverageDto; 
    setIsFormSaved?: (isSaved: boolean) => void;
    formValidation?: any;
    tabIndex?: number;
}

const CustomerFloodCoverageForm: React.FunctionComponent<ICustomerFloodCoverageForm> = (props) => {
    const { customerFloodCoverage, setIsFormSaved, floodCoverageId, polEventId, polId, formValidation: externalFormValidation, tabIndex } = props;
    let tbIndex = tabIndex;
 
    const dispatch = useDispatch();
    const customerDetail = useSelector((state: RootState) => state?.customerSlice);
    const policyDetailKey = CommonUtility.getPolicyDetailKey(polId, polEventId);
    const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
    
    const [formValidation, setFormValidation] = useState<any>(externalFormValidation || {});
    const [floodCoverageData, setFloodCoverageData] = useState<PolicyFloodCoverageDto>(customerFloodCoverage || {});

    useEffect(() => {
        if (externalFormValidation) {
            setFormValidation(externalFormValidation);
        }
    }, [externalFormValidation]);

    // Update form data when the coverage prop changes
    useEffect(() => {
        if(policyDetails && policyDetails.policyLineOfBusinesses) {
            const lob = policyDetails.policyLineOfBusinesses.find(l => l.polEventId === polEventId);
            if (lob && lob.policyFloodCoverages) {
                const coverage = lob.policyFloodCoverages.find(c => c.id === floodCoverageId);
                if (coverage) {
                    setFloodCoverageData(coverage);
                }
            }
        }
    }, [floodCoverageId]);

    const onEntityFieldChange = (propName: string, value: string | number | null) => {
        // Validate decimal input if value is a string
        if (typeof value === 'string' && !CommonUtility.isValidDecimalUpto2Limit(value)) {
            return;
        }
        
        let processedValue = value;
        if (typeof value === 'string') {
            processedValue = value === '' ? 0 : (value.endsWith('.') || value === '.' ? value : parseFloat(value));
        }
        
        const updatedData = {
            ...floodCoverageData,
            [propName]: processedValue,
        };
        setFloodCoverageData(updatedData);
        
        const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
        
        if (updatedPolicyDetails && updatedPolicyDetails.policyLineOfBusinesses) {
            const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map(lob => {
                // Only update LOBs matching our policy event ID
                if (lob.polEventId === polEventId) {
                    // Check if the coverage exists
                    const coverageExists = lob.policyFloodCoverages.some(cov => cov.id === floodCoverageId);
                    
                    if (coverageExists) {
                        // If coverage exists, update it
                        return {
                            ...lob,
                            policyFloodCoverages: lob.policyFloodCoverages.map((cov: PolicyFloodCoverageDto) => {
                                if (cov.id === floodCoverageId) {
                                    return {
                                        ...cov,
                                        ...updatedData,
                                    };
                                } else {
                                    return cov;
                                }
                            }),
                        };
                    } else {
                        // If coverage doesn't exist, add it to the array
                        return {
                            ...lob,
                            policyFloodCoverages: [
                                {
                                    id: floodCoverageId,
                                    ...updatedData
                                },
                                ...lob.policyFloodCoverages
                            ]
                        };
                    }
                }
                return lob;
            });
            
            updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;

            // Dispatch the updated policy details to the store
            dispatch(updateSelectedPolicyDetails({ 
                policyId: polId, 
                polEventId: polEventId, 
                policyDetails: updatedPolicyDetails 
            }));
        }

        // Mark form as modified
        if (setIsFormSaved) {
            setIsFormSaved(false);
        }
    };

    return (
        <div className="main-cst-coverage-div">
            <Row>
                <Column lg={12} md={12}>
                    <Row className="mb-3">
                        
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="buildingLimit1"
                                className="mb-3"
                                label="Building Limit1"
                            >
                                <Form.Control
                                    type="number"
                                    name="buildingLimit1"
                                    placeholder="Building Limit1"
                                    value={floodCoverageData.buildingLimit1 || ''}
                                    onChange={(event) => onEntityFieldChange('buildingLimit1', event.target.value ? parseInt(event.target.value) : 0)}
                                    tabIndex={tbIndex}
                                    isInvalid={formValidation['buildingLimit1']?.isValid === false}
                                />
                                 {formValidation['buildingLimit1']?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation['buildingLimit1']?.warningText}
                                    </Form.Control.Feedback>
                                )} 
                            </FloatingLabel>
                        </Column>
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="buildingPremium"
                                className="mb-3"
                                label="Building Premium"
                            >
                                <Form.Control
                                    type="text"
                                    name="buildingPremium"
                                    placeholder="Building Premium"
                                    value={floodCoverageData.buildingPremium || ''}
                                    onChange={(event) => onEntityFieldChange('buildingPremium', event.target.value)}
                                    // maxLength={30}
                                    tabIndex={tbIndex}
                                    isInvalid={formValidation['buildingPremium']?.isValid === false}
                                />
                                {formValidation['buildingPremium']?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation['buildingPremium']?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="contentLimit1"
                                className="mb-3"
                                label="Content Limit1"
                            >
                                <Form.Control
                                    type="number"
                                    name="contentLimit1"
                                    placeholder="Content Limit1"
                                    value={floodCoverageData.contentLimit1 || ''}
                                    onChange={(event) => onEntityFieldChange('contentLimit1', event.target.value ? parseInt(event.target.value) : 0)}
                                    tabIndex={tbIndex}
                                    // isInvalid={formValidation['limit2']?.isValid === false}
                                />
                            </FloatingLabel>
                        </Column>
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="contentPremium"
                                className="mb-3"
                                label="Content Premium"
                            >
                                <Form.Control
                                    type="text"
                                    name="contentPremium"
                                    placeholder="Content Premium"
                                    value={floodCoverageData.contentPremium || ''}
                                    onChange={(event) => onEntityFieldChange('contentPremium', event.target.value)}
                                    tabIndex={tbIndex}
                                    // isInvalid={formValidation['limit2']?.isValid === false}
                                />
                            </FloatingLabel>
                        </Column>
                    </Row>

                    {/* Second row  */}
                    <Row className="mb-3">
                        
                        
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="additionalCoverageTotal"
                                className="mb-3"
                                label="Additional Coverage Total"
                            >
                                <Form.Control
                                    type="text"
                                    name="additionalCoverageTotal"
                                    placeholder="Additional Coverage Total"
                                    value={floodCoverageData.additionalCoverageTotal || ''}
                                    onChange={(event) => onEntityFieldChange('additionalCoverageTotal', event.target.value)}
                                    // maxLength={30}
                                    tabIndex={tbIndex}
                                    isInvalid={formValidation['additionalCoverageTotal']?.isValid === false}
                                />
                                {formValidation['additionalCoverageTotal']?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation['additionalCoverageTotal']?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="discountSurchargesTotal"
                                className="mb-3"
                                label="Discount Surcharges Total"
                            >
                                <Form.Control
                                    type="text"
                                    name="discountSurchargesTotal"
                                    placeholder="Discount Surcharges Total"
                                    value={floodCoverageData.discountSurchargesTotal || ''}
                                    onChange={(event) => onEntityFieldChange('discountSurchargesTotal', event.target.value)}
                                    tabIndex={tbIndex}
                                    // isInvalid={formValidation['limit2']?.isValid === false}
                                />
                            </FloatingLabel>
                        </Column>
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="prePaidTotal"
                                className="mb-3"
                                label="Prepaid Total"
                            >
                                <Form.Control
                                    type="text"
                                    name="prePaidTotal"
                                    placeholder="Prepaid Total"
                                    value={floodCoverageData.prePaidTotal || ''}
                                    onChange={(event) => onEntityFieldChange('prePaidTotal', event.target.value)}
                                    // maxLength={30}
                                    tabIndex={tbIndex}
                                    isInvalid={formValidation['prePaidTotal']?.isValid === false}
                                />
                                {formValidation['prePaidTotal']?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation['prePaidTotal']?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                         <Column lg={3} md={3}>
                        <FloatingLabel
                                controlId="annualTotal"
                                className="mb-3"
                                label="Annual Total"
                            >
                                <Form.Control
                                    type="text"
                                    name="annualTotal"
                                    placeholder="Annual Total"
                                    value={floodCoverageData.annualTotal || ''}
                                    onChange={(event) => onEntityFieldChange('annualTotal', event.target.value)}
                                    tabIndex={tbIndex}
                                    isInvalid={formValidation['annualTotal']?.isValid === false}
                                />
                                 {formValidation['annualTotal']?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation['annualTotal']?.warningText}
                                    </Form.Control.Feedback>
                                )} 
                            </FloatingLabel>
                        </Column>
                    </Row>

                    {/* Third row */}
                    <Row className="mb-3">
                        <Column lg={3} md={3}>
                            <FloatingLabel
                                controlId="floodDeductible"
                                className="mb-3"
                                label="Flood Deductible"
                            >
                                <Form.Control
                                    type="text"
                                    name="floodDeductible"
                                    placeholder="Flood Deductible"
                                    value={floodCoverageData.floodDeductible || ''}
                                    onChange={(event) => onEntityFieldChange('floodDeductible', event.target.value)}
                                    tabIndex={tbIndex}
                                    isInvalid={formValidation['floodDeductible']?.isValid === false}
                                />
                                {formValidation['floodDeductible']?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation['floodDeductible']?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                    </Row>
                </Column>
            </Row>
        </div>
    );
};

export default CustomerFloodCoverageForm;